import {
   AppointmentAlreadyCancelledFault,
   Visit,
   VisitAlreadyStartedFault,
   VisitAndAppointmentView,
   VisitStartCommand,
} from '@dreryk/egabinet-visit-frontend-api';
import { ConfidentialityLevel } from '@dreryk/egabinet-medical-data-confidentiality-published-language-api';

import {
   ActionBar,
   ActionBarElementType,
   ActionEventInterceptor,
   ButtonVariant,
   dispatchSnackbarInformativeEvent,
   useHistoryPushWithBackAction,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';

import { useCurrentPractitionerConfidentialityMode } from '~libs.shared.account';
import { visitPaths } from '~libs.desktop.shared.paths';
import { VisitEnterButton } from '~libs.desktop.shared.visit-enter';

import { usePatientUpdateCommand } from '../PatientConfirmedUpdatePanel/PatientConfirmedUpdatePersonalData/PatientConfirmedUpdateAction';
import { patientTranslationKeys } from '../translations';
import { usePatientDependenciesContext } from '../dependencies';

import { PatientUnconfirmedFormController } from './patientUnconfirmedUpdate.types';

type VisitStartCommandParameters = {
   appointmentId: VisitAndAppointmentView['appointmentId'];
};

type VisitStartCommandOptions = {
   confidentialityLevel: ConfidentialityLevel;
   onSuccess: VoidFunction;
};

export const useVisitStartCommand = (
   options: VisitStartCommandOptions
): UseCqrsCommandResult<
   Visit['id'],
   VisitStartCommandParameters,
   VisitAlreadyStartedFault | AppointmentAlreadyCancelledFault
> => {
   const push = useHistoryPushWithBackAction(true);

   return useCqrsCommand(
      (parameters) => {
         const command = new VisitStartCommand();
         command.appointmentId = parameters.appointmentId;
         command.confidentialityLevel = options.confidentialityLevel;
         return command;
      },
      {
         onSuccess: (visitId) => {
            options.onSuccess();
            push(visitPaths.getVisitInterviewPath({ params: { visitId: visitId } }), {});
         },
         onFault: {
            'egabinet-visit-frontend-api:VisitAlreadyStartedFault': () => {
               dispatchSnackbarInformativeEvent(
                  patientTranslationKeys.unconfirmed.saveAndStartVisit.exceptions.alreadyStarted
               );
            },
            'egabinet-visit-frontend-api:AppointmentAlreadyCancelledFault': () => {
               dispatchSnackbarInformativeEvent(
                  patientTranslationKeys.unconfirmed.saveAndStartVisit.exceptions.alreadyCancelled
               );
            },
         },
      }
   );
};

type PatientUnconfirmedUpdateAndStartVisitActionProps = {
   readonly patientId: number;
   readonly appointmentId: number;
   readonly handleSubmit: PatientUnconfirmedFormController['handleSubmit'];
   readonly trigger: PatientUnconfirmedFormController['trigger'];
   readonly onSuccess: VoidFunction;
};
export const PatientUnconfirmedUpdateAndStartVisitAction = (
   props: PatientUnconfirmedUpdateAndStartVisitActionProps
): JSX.Element => {
   const { accountConfigurationVerificationEventInterceptor } = usePatientDependenciesContext();
   const { executeAsync: startVisit } = useVisitStartCommand({
      confidentialityLevel: useCurrentPractitionerConfidentialityMode(),
      onSuccess: props.onSuccess,
   });
   const { executeAsync: updatePatient } = usePatientUpdateCommand(props.patientId, null);

   return (
      <ActionBar.Portal elementType={ActionBarElementType.PRIMARY} order={1}>
         <ValidateFormEventInterceptor trigger={props.trigger} silent>
            <ActionEventInterceptor action={props.handleSubmit(updatePatient)}>
               <VisitEnterButton
                  appointmentId={props.appointmentId}
                  patientId={props.patientId}
                  label={patientTranslationKeys.unconfirmed.saveAndStartVisit.label}
                  onClick={async () => startVisit({ appointmentId: props.appointmentId })}
                  testId='updateAndStartVisit'
                  accountConfigurationVerificationEventInterceptor={accountConfigurationVerificationEventInterceptor}
                  compact
                  variant={ButtonVariant.PRIMARY}
               />
            </ActionEventInterceptor>
         </ValidateFormEventInterceptor>
      </ActionBar.Portal>
   );
};
